import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit, Trash2, Eye, EyeOff, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBlog = () => {
  const { token } = useAuth();
  const { refreshStats } = useOutletContext();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    title_es: '',
    slug: '',
    excerpt: '',
    excerpt_es: '',
    content: '',
    content_es: '',
    tag: '',
    featured_image: '',
    published: true
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/blog/posts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      toast.error('Error loading posts');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: editingPost ? prev.slug : generateSlug(title)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingPost 
        ? `${API_URL}/api/admin/blog/posts/${editingPost.id}`
        : `${API_URL}/api/admin/blog/posts`;
      
      const response = await fetch(url, {
        method: editingPost ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingPost ? 'Post updated!' : 'Post created!');
        setShowModal(false);
        resetForm();
        fetchPosts();
        refreshStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error saving post');
      }
    } catch (error) {
      toast.error('Error saving post');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/blog/posts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Post deleted!');
        fetchPosts();
        refreshStats();
      }
    } catch (error) {
      toast.error('Error deleting post');
    }
  };

  const openEditModal = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      title_es: post.title_es || '',
      slug: post.slug,
      excerpt: post.excerpt,
      excerpt_es: post.excerpt_es || '',
      content: post.content,
      content_es: post.content_es || '',
      tag: post.tag,
      featured_image: post.featured_image || '',
      published: post.published
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      title_es: '',
      slug: '',
      excerpt: '',
      excerpt_es: '',
      content: '',
      content_es: '',
      tag: '',
      featured_image: '',
      published: true
    });
  };

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.tag.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-terracota border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blog">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-serif text-white mb-1">Blog Posts</h1>
          <p className="text-stone-400 text-sm">{posts.length} total posts</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-terracota text-white px-4 py-2 rounded-lg hover:bg-terracota/90 transition-colors"
          data-testid="add-blog-post-btn"
        >
          <Plus className="w-5 h-5" />
          New Post
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
        <input
          type="text"
          placeholder="Search posts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-stone-800 border border-stone-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-stone-500 focus:outline-none focus:border-terracota"
        />
      </div>

      {/* Posts List */}
      <div className="space-y-4">
        {filteredPosts.length === 0 ? (
          <div className="bg-stone-800 rounded-xl p-8 text-center border border-stone-700">
            <p className="text-stone-400">No posts found</p>
          </div>
        ) : (
          filteredPosts.map((post) => (
            <div 
              key={post.id}
              className="bg-stone-800 rounded-xl p-4 border border-stone-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-medium truncate">{post.title}</h3>
                  {!post.published && (
                    <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">Draft</span>
                  )}
                </div>
                <p className="text-stone-400 text-sm truncate">{post.excerpt}</p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs bg-stone-700 text-stone-300 px-2 py-1 rounded">{post.tag}</span>
                  <span className="text-xs text-stone-500">/{post.slug}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEditModal(post)}
                  className="p-2 text-stone-400 hover:text-white transition-colors"
                  title="Edit"
                >
                  <Edit className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(post.id)}
                  className="p-2 text-stone-400 hover:text-red-400 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-stone-800 rounded-xl w-full max-w-3xl my-8 border border-stone-700">
            <div className="flex items-center justify-between p-4 border-b border-stone-700">
              <h2 className="text-lg font-semibold text-white">
                {editingPost ? 'Edit Post' : 'New Post'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-stone-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Title (EN)</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    required
                  />
                </div>
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Title (ES)</label>
                  <input
                    type="text"
                    value={formData.title_es}
                    onChange={(e) => setFormData({ ...formData, title_es: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    required
                  />
                </div>
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Tag</label>
                  <input
                    type="text"
                    value={formData.tag}
                    onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-stone-400 text-sm mb-1">Featured Image URL</label>
                <input
                  type="url"
                  value={formData.featured_image}
                  onChange={(e) => setFormData({ ...formData, featured_image: e.target.value })}
                  className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                  placeholder="https://..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Excerpt (EN)</label>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    rows={2}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota resize-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Excerpt (ES)</label>
                  <textarea
                    value={formData.excerpt_es}
                    onChange={(e) => setFormData({ ...formData, excerpt_es: e.target.value })}
                    rows={2}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota resize-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-stone-400 text-sm mb-1">Content (EN) - Markdown supported</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={8}
                  className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota resize-none font-mono text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-stone-400 text-sm mb-1">Content (ES) - Markdown supported</label>
                <textarea
                  value={formData.content_es}
                  onChange={(e) => setFormData({ ...formData, content_es: e.target.value })}
                  rows={8}
                  className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota resize-none font-mono text-sm"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="published"
                  checked={formData.published}
                  onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  className="w-4 h-4 rounded bg-stone-700 border-stone-600 text-terracota focus:ring-terracota"
                />
                <label htmlFor="published" className="text-stone-300 text-sm">Published</label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-stone-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-stone-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-terracota text-white rounded-lg hover:bg-terracota/90 transition-colors"
                >
                  {editingPost ? 'Update Post' : 'Create Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
